import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInstallerTasksStore } from '@/stores/installerTasks';
import type { InstallerTask } from '@/types/odoo';
import { InterventieBadge } from '@/components/interventie/InterventieBadge';
import { useAanwezigheid, WerkersPicker } from '@/components/installer/WerkersPicker';
import { usePermissionsStore } from '@/stores/permissions';
import { useMyLimosa } from '@/components/limosa/useMyLimosa';
import { JobCardLimosa } from '@/components/limosa/JobCardLimosa';

const DUTCH_DAYS = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];

function getDateForOffset(offset: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offset);
  return d;
}

function formatDayLabel(offset: number): string {
  if (offset === 0) return 'Vandaag';
  if (offset === 1) return 'Morgen';
  if (offset === -1) return 'Gisteren';
  const d = getDateForOffset(offset);
  const dayName = DUTCH_DAYS[d.getDay()];
  const day = d.getDate();
  const month = d.toLocaleDateString('nl-BE', { month: 'long' });
  return `${dayName} ${day} ${month}`;
}

function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
      <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
      <div className="h-4 bg-gray-100 rounded w-1/2 mb-3" />
      <div className="flex gap-2">
        <div className="h-6 bg-gray-100 rounded w-16" />
        <div className="h-6 bg-gray-100 rounded w-20" />
      </div>
    </div>
  );
}

function TaskCard({ task }: { task: InstallerTask }) {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      onClick={() => navigate(`/installer/project/${task.projectId}?task=${task.id}`, { state: { projectName: task.projectName, werfLocation: task.werfLocation, checkinAtWork: task.checkinAtWork } })}
      className="w-full text-left p-4 hover:bg-gray-50 active:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-td-blue transition-colors min-h-[48px]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-td-navy truncate">
            {task.projectName}
          </p>
          <p className="text-sm text-td-navy/70 mt-0.5 truncate">
            {task.isInterventie && <InterventieBadge className="mr-1.5 align-middle" />}
            {task.name}
          </p>
          <p className="text-xs text-td-muted mt-0.5 truncate">
            {task.werfLocation}
          </p>
          {task.projectleiderName && (
            <p className="text-xs text-td-muted mt-0.5 truncate">
              PL: {task.projectleiderName}
            </p>
          )}
        </div>
        <svg className="w-5 h-5 text-gray-300 shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );
}

export function InstallerTaskListView() {
  const { tasks, loading, error, dayOffset, fetchTasks, setDayOffset, lastRefreshed } = useInstallerTasksStore();

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Auto-refresh: on tab visibility change + 60s interval while visible
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;

    function startInterval() {
      if (!intervalId) {
        intervalId = setInterval(() => fetchTasks(), 60_000);
      }
    }

    function stopInterval() {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        fetchTasks();
        startInterval();
      } else {
        stopInterval();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start interval if tab is already visible
    if (document.visibilityState === 'visible') {
      startInterval();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopInterval();
    };
  }, [fetchTasks]);

  const selectedDate = useMemo(() => getDateForOffset(dayOffset), [dayOffset]);
  const selectedDateKey = useMemo(() => formatDateKey(selectedDate), [selectedDate]);

  const aanwezigheid = useAanwezigheid(selectedDateKey);
  // Limosa is for onderaannemers; employee installers never need one.
  const isOa = usePermissionsStore((s) => s.permissions?.isOa ?? false);
  const limosa = useMyLimosa(selectedDateKey, isOa);

  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => t.dateDeadline.split(' ')[0] === selectedDateKey);
  }, [tasks, selectedDateKey]);

  return (
    <div className="px-4 py-5 max-w-lg lg:max-w-4xl mx-auto">
      {/* Title + Refresh */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-extrabold text-td-navy">Planning</h1>
        <button
          type="button"
          onClick={() => fetchTasks()}
          disabled={loading}
          className="p-2 rounded-lg text-td-muted hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-50 min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Vernieuwen"
        >
          <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Day selector */}
      <div className="flex items-center justify-between mb-5">
        <button
          type="button"
          onClick={() => setDayOffset(dayOffset - 1)}
          disabled={dayOffset <= -30}
          className="p-2 rounded-lg text-td-muted hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-30 min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Vorige dag"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="text-center">
          <p className="text-sm font-bold text-td-navy">{formatDayLabel(dayOffset)}</p>
          <p className="text-xs text-td-muted">{selectedDateKey}</p>
        </div>

        <button
          type="button"
          onClick={() => setDayOffset(dayOffset + 1)}
          disabled={dayOffset >= 30}
          className="p-2 rounded-lg text-td-muted hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-30 min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Volgende dag"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Last refreshed */}
      {lastRefreshed && (
        <p className="text-xs text-td-muted text-center mb-4">
          Laatst vernieuwd om {lastRefreshed.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}
        </p>
      )}

      {/* Loading */}
      {loading && tasks.length === 0 && (
        <div className="space-y-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
          <p className="font-semibold">Fout bij laden</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            type="button"
            onClick={() => fetchTasks()}
            className="mt-3 text-sm font-bold text-red-700 hover:text-red-800 underline min-h-[48px]"
          >
            Opnieuw proberen
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filteredTasks.length === 0 && (
        <div className="text-center py-16">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-td-muted text-sm font-medium">Geen taken voor deze dag</p>
        </div>
      )}

      {/* Tasks */}
      {!error && filteredTasks.length > 0 && (
        <div className="space-y-4">
          {filteredTasks.map((task) => (
            <div key={task.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden has-[>button:hover]:border-td-blue/40 has-[>button:hover]:shadow-sm transition-all" data-testid="job-card">
              <TaskCard task={task} />
              {/* Presence and Limosa as a section of the same card, under a divider; hidden when both render nothing. */}
              <div className="border-t border-gray-100 pt-3 empty:hidden" data-testid="job-card-presence">
                {aanwezigheid.data && (
                  <WerkersPicker taskId={task.id} date={selectedDateKey} aanwezigheid={aanwezigheid.data} error={aanwezigheid.error} onToggle={aanwezigheid.toggle} />
                )}
                {limosa.data && (
                  <JobCardLimosa taskId={task.id} date={selectedDateKey} limosa={limosa.data} aanwezigheid={aanwezigheid.data} onUploaded={limosa.reload} />
                )}
                {limosa.error && <p className="px-4 pb-3 text-xs text-red-600" role="alert">{limosa.error}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
